// ── Tenant Guard Layer ──
// Centralized, mandatory tenant isolation enforcement.
// All business-critical queries MUST pass through these guards.
// This is the hardening layer — no raw record access should bypass this.

import type { TenantOwned, TenantAccessResult } from "./types";
import type { ManagedUser } from "@/data/controlPlane/types";

// ── Secure Query Guard ──

export interface TenantGuardContext {
  userTenantId: string | null;
  isCrossTenant: boolean;
  role: string;
}

/**
 * The canonical secure query function.
 * ALL tenant-owned record access MUST pass through this.
 * 
 * Rules:
 * - Cross-tenant operators (null tenantId) see everything
 * - Scoped users only see their tenant's records + legacy (no tenantId)
 * - Every query is logged for audit traceability
 */
export function secureTenantQuery<T extends TenantOwned>(
  guard: TenantGuardContext,
  records: T[]
): { records: T[]; meta: QueryMeta } {
  const start = records.length;

  if (guard.isCrossTenant && !guard.userTenantId) {
    // Cross-tenant mode — return all, but flag it
    return {
      records,
      meta: {
        mode: "cross_tenant",
        inputCount: start,
        outputCount: start,
        filtered: 0,
        tenantId: null,
      },
    };
  }

  const tenantId = guard.userTenantId;
  if (!tenantId) {
    return {
      records,
      meta: { mode: "unscoped", inputCount: start, outputCount: start, filtered: 0, tenantId: null },
    };
  }

  const filtered = records.filter((r) => !r.tenantId || r.tenantId === tenantId);
  return {
    records: filtered,
    meta: {
      mode: "tenant_scoped",
      inputCount: start,
      outputCount: filtered.length,
      filtered: start - filtered.length,
      tenantId,
    },
  };
}

export interface QueryMeta {
  mode: "cross_tenant" | "tenant_scoped" | "unscoped";
  inputCount: number;
  outputCount: number;
  filtered: number;
  tenantId: string | null;
}

// ── Cross-Tenant Safety ──

export type DestructiveAction =
  | "capability_override"
  | "user_role_change"
  | "user_status_change"
  | "tenant_capability_override"
  | "tenant_profile_change";

/**
 * Determines if a destructive action requires explicit confirmation
 * based on the current tenant context.
 */
export function requiresCrossTenantConfirmation(
  guard: TenantGuardContext,
  action: DestructiveAction
): { required: boolean; reason: string } {
  if (!guard.isCrossTenant || guard.userTenantId) {
    return { required: false, reason: "Operating within single tenant" };
  }

  // In "All Companies" mode, ALL destructive actions require confirmation
  return {
    required: true,
    reason: `Action "${action}" affects cross-tenant scope. Explicit confirmation required.`,
  };
}

// ── Dev-Mode Integrity Checks ──

export interface IntegrityWarning {
  recordId: string;
  recordType: string;
  issue: string;
  severity: "warning" | "error";
}

/**
 * Scans records for missing tenant ownership.
 * Only runs in dev mode. Logs warnings for migration tracking.
 */
export function checkTenantIntegrity<T extends TenantOwned & { id: string }>(
  records: T[],
  recordType: string
): IntegrityWarning[] {
  const warnings: IntegrityWarning[] = [];

  for (const record of records) {
    if (!record.tenantId) {
      warnings.push({
        recordId: record.id,
        recordType,
        issue: `Missing tenantId — record is unscoped (legacy)`,
        severity: "warning",
      });
    }
  }

  if (warnings.length > 0 && import.meta.env.DEV) {
    console.warn(
      `[TenantIntegrity] ${warnings.length} ${recordType} records missing tenantId:`,
      warnings.map((w) => w.recordId)
    );
  }

  return warnings;
}

// ── Tenant Audit Tagging ──

export interface TenantAuditTag {
  tenantId: string | null;
  tenantName: string | null;
  crossTenantAction: boolean;
  operatorTenantId: string | null;
}

/**
 * Generates audit metadata for any action that affects tenant-scoped data.
 * This MUST be attached to every audit log entry.
 */
export function buildTenantAuditTag(
  guard: TenantGuardContext,
  targetTenantId: string | null,
  targetTenantName: string | null
): TenantAuditTag {
  return {
    tenantId: targetTenantId,
    tenantName: targetTenantName,
    crossTenantAction: guard.isCrossTenant && !guard.userTenantId,
    operatorTenantId: guard.userTenantId,
  };
}
